<script lang="ts">
	import classNames from 'classnames';
	import { CalendarIcon } from '../../../../assets/icons/CalendarIcon';
	import { MarkerIcon } from '../../../../assets/icons/MarkerIcon';
	import { Link } from '../../../../components/Link';
	import { Txt } from '../../../../components/Txt';
	import { CARD_IMAGE_SIZES } from '../../../../constants/card-image-sizes';
	import { getDownscaledImageSrcset } from '../../../../utils/getDownscaledImageSrcset/getDownscaledImageSrcset';

	interface TitleImage {
		line1: string;
		line2?: string | undefined;
	}

	interface Exhibition {
		secondaryButtonProps?:
			| {
					href: string;
			  }
			| undefined;
		src?: string | undefined;
		title?: string | undefined;
		subtitle?: string | undefined;
		dates: string;
		short_location_description: string;
		titleImage?: TitleImage;
	}

	interface Props {
		exhibition: Exhibition;
		dataCy: string;
		class?: string;
		/** Load the image eagerly at high priority, for a card that is above the fold. */
		priority?: boolean;
		/** `sizes` for the image's srcset; defaults to full width on a phone, half from md. */
		sizes?: string;
	}

	let { priority = false, sizes = CARD_IMAGE_SIZES, ...props }: Props = $props();

	let dataCyPrefix = $derived(`${props.dataCy}-exhibition-card`);
	let imageLoadingProps = $derived(
		priority
			? { fetchpriority: 'high' as const, loading: 'eager' as const }
			: { loading: 'lazy' as const }
	);
</script>

<Link
	href={props.exhibition.secondaryButtonProps?.href}
	class={classNames('rounded border hover:border-gray-400', props.class)}
>
	<div class="h-[14rem] sm:h-[10rem] md:h-[8.5rem] lg:h-[10rem]">
		{#if props.exhibition.titleImage}
			<div class="h-full w-full px-2 bg-gray-100 flex flex-col items-center justify-center border">
				<Txt
					variant="h6"
					align="center"
					class="text-ellipsis overflow-hidden line-clamp-3"
					data-cy={`${dataCyPrefix}-line1-image`}
				>
					{props.exhibition.titleImage.line1}
				</Txt>
				{#if props.exhibition.titleImage.line2}
					<Txt
						variant="body2"
						align="center"
						class="text-gray-500"
						data-cy={`${dataCyPrefix}-line2-image`}
					>
						{props.exhibition.titleImage.line2}
					</Txt>
				{/if}
			</div>
		{:else}
			<img
				data-cy={`${dataCyPrefix}-image`}
				src={props.exhibition.src}
				srcset={getDownscaledImageSrcset(props.exhibition.src)}
				{sizes}
				decoding="async"
				{...imageLoadingProps}
				alt={props.exhibition.title}
				class="h-full w-full rounded-t object-cover"
			/>
		{/if}
	</div>
	<div
		class="flex h-[calc(100%-14rem)] flex-col justify-between p-4 sm:h-[calc(100%-10rem)] md:h-[calc(100%-8.5rem)] lg:h-[calc(100%-10rem)]"
	>
		<Txt variant="h6" class="mb-1" data-cy={`${dataCyPrefix}-title`}>
			{props.exhibition.subtitle}
		</Txt>
		<div class="mt-1">
			<Txt variant="body2" class="mb-1 flex items-start" data-cy={`${dataCyPrefix}-start-date`}>
				<CalendarIcon class="mr-2 mt-[-0.125rem] min-w-6 min-h-6" />
				{props.exhibition.dates}
			</Txt>
			<Txt variant="body2" class="flex items-center" data-cy={`${dataCyPrefix}-location`}>
				<MarkerIcon class="mr-2 w-6 h-6 flex-shrink-0" />
				{props.exhibition.short_location_description}
			</Txt>
		</div>
	</div>
</Link>
